import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { fcfa, ttc } from "@/lib/format";

const INTERVAL_MS = 3000;

const HeroCarousel = ({ hero = {}, slides = [] }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;

  const next = useCallback(() => setIndex((i) => (count ? (i + 1) % count : 0)), [count]);
  const prev = useCallback(() => setIndex((i) => (count ? (i - 1 + count) % count : 0)), [count]);

  useEffect(() => {
    if (paused || count < 2) return undefined;
    const t = setInterval(next, INTERVAL_MS);
    return () => clearInterval(t);
  }, [next, paused, count]);

  if (!count) return null;

  const current = slides[index];
  const heroLines = (hero.title || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const showOverlay = hero.enabled !== false;

  return (
    <section
      data-testid="home-hero"
      className="relative h-[78vh] min-h-[540px] w-full overflow-hidden bg-bark"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <AnimatePresence mode="popLayout">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={current.image}
            alt={current.name}
            data-testid={`hero-slide-${index}`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-bark/90 via-bark/35 to-bark/10" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-20 lg:px-10">
        {showOverlay && hero.eyebrow && (
          <p data-testid="home-hero-eyebrow" className="mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-sand/70">
            {hero.eyebrow}
          </p>
        )}
        {showOverlay && heroLines.length > 0 ? (
          <h1 data-testid="home-hero-title" className="max-w-3xl font-display text-4xl font-bold leading-[1.04] tracking-tight text-sand sm:text-6xl lg:text-7xl">
            {heroLines.map((line, i) => (
              <span key={i} className={`block ${i === heroLines.length - 1 && heroLines.length > 1 ? "italic text-terracotta-light" : ""}`}>
                {line}
              </span>
            ))}
          </h1>
        ) : (
          <h1 data-testid="home-hero-title" className="max-w-3xl font-display text-4xl font-bold leading-[1.04] tracking-tight text-sand sm:text-6xl">
            {current.name}
          </h1>
        )}
        {showOverlay && hero.text && (
          <p data-testid="home-hero-text" className="mt-5 max-w-md text-sm leading-relaxed text-sand/75 sm:text-base">
            {hero.text}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-4">
          <Link
            to="/boutique"
            data-testid="hero-cta-boutique"
            className="group flex h-13 items-center gap-3 rounded-full bg-terracotta px-7 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta-dark"
          >
            Découvrir la boutique
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to={`/produit/${current.id}`}
            data-testid="hero-cta-product"
            className="flex items-center gap-3 rounded-full border border-sand/30 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-sand backdrop-blur-sm transition-colors duration-300 hover:border-terracotta-light hover:text-terracotta-light"
          >
            {current.name} — {fcfa(ttc(current))}
          </Link>
        </div>
      </div>

      {count > 1 && (
        <>
          <button
            data-testid="hero-carousel-prev"
            onClick={prev}
            aria-label="Produit précédent"
            className="absolute left-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-sand/30 text-sand backdrop-blur-sm transition-colors hover:border-terracotta-light hover:text-terracotta-light md:flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid="hero-carousel-next"
            onClick={next}
            aria-label="Produit suivant"
            className="absolute right-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-sand/30 text-sand backdrop-blur-sm transition-colors hover:border-terracotta-light hover:text-terracotta-light md:flex"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div data-testid="hero-carousel-dots" className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id}
                data-testid={`hero-carousel-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Aller au produit ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-terracotta-light" : "w-2 bg-sand/40 hover:bg-sand/70"}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HeroCarousel;
